<template>
  <div>
    <toastification-loading-content ref="loadingToast" />
    <toastification-progress-content ref="progressToast" />

    <sidenav
      :is-sidebar-active.sync="isSidebarActive"
      :item-edit.sync="itemEdit"
      :is-add.sync="isAdd"
      @refetch-data="refetchData"
      :facilities-options="facilitiesOptions"
      :center-options="centerOptions"
      :sector-options="sectorOptions"
    />
    <b-row class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <filters
          :search-query.sync="searchQuery"
          :facilities="facilitiesOptions"
          :facilities-filter.sync="facilitiesFilter"
          :centers="centerOptions"
          :center-filter.sync="centerFilter"
          :sectors="sectorOptions"
          :sector-filter.sync="sectorFilter"
        />
      </template>
    </filter-modal>

    <!-- Table Container Card -->
    <b-card class="mx-2" no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="text-capitalize">{{ $t("table.show") }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label class="text-capitalize">{{ $t("table.entries") }}</label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                :placeholder="$t('table.search')"
              />
              <b-button
                variant="primary"
                @click="openAdd()"
                v-if="$can('create', 'Intelbras')"
              >
                <span class="text-nowrap text-capitalize">{{
                  `${$t("actions.add")} ${$tc("intelbras.title", 1)}`
                }}</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refListTable"
        class="position-relative"
        :items="fetchList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        :sort-desc.sync="isSortDirDesc"
        show-empty
        :empty-text="$t('no_record_found')"
      >
        <template #head()="data">
          <span> {{ $tc(data.label, 1) }}</span>
        </template>

        <template #cell(panel_status)="data">
          <div
            :style="`background: ${data.item.device.online ? 'green' : 'red'};
                width: 1.5rem;
                height: 1.5rem;
                border-radius: 1rem;
                text-align: center;`"
            :title="
              $t(`intelbras.${data.item.device.online ? 'connected' : 'disconnected'}`)
            "
          ></div>
        </template>

        <template #cell(active)="data">
          <div class="text-center">
            <button
              v-if="!data.item.active"
              class="btn btn-sm btn-success"
              :disabled="loading"
              @click="armPanel(data.item)"
            >
              {{ $t("intelbras.arm") }}
            </button>
            <button
              v-if="data.item.active"
              class="btn btn-sm btn-orange"
              :disabled="loading"
              @click="armPanel(data.item)"
            >
              {{ $t("intelbras.disarm") }}
            </button>
          </div>
        </template>

        <template #head(name)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #cell(unit.description)="data">
          {{ unitsSite(data.item) }}
        </template>

        <template #head(center.name)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(sector.name)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #cell(uuid)="data">
          <div
            v-clipboard:copy="`${data.item.uuid}`"
            v-clipboard:success="onCopy"
            v-clipboard:error="onError"
            style="cursor: pointer"
          >
            <feather-icon icon="CopyIcon" size="16" class="align-middle text-body" />
          </div>
        </template>

        <template #cell(client_password)="data">
          <div
            style="cursor: pointer"
            @click="copyToClipboard(data.item.id, 'client_password')"
          >
            <feather-icon icon="CopyIcon" size="16" class="align-middle text-body" />
          </div>
        </template>

        <template #cell(remote_cfg_password)="data">
          <div
            style="cursor: pointer"
            @click="copyToClipboard(data.item.id, 'remote_cfg_password')"
          >
            <feather-icon icon="CopyIcon" size="16" class="align-middle text-body" />
          </div>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-button
            :id="`actions-${data.item.id}`"
            tabindex="0"
            variant="white"
            class="px-1 btn-y"
          >
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </b-button>
          <b-popover
            :target="`actions-${data.item.id}`"
            triggers="focus"
            placement="bottom"
            custom-class="popover-p-0"
            variant="white"
          >
            <b-list-group class="m-0">
              <b-list-group-item
                v-if="$can('update', 'Intelbras')"
                class="border-0"
                :to="`/intelbras/panels/${data.item.id}/devices`"
              >
                <feather-icon icon="HardDriveIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("intelbras.devices") }}
                </span>
              </b-list-group-item>

              <b-list-group-item
                v-if="$can('update', 'Intelbras')"
                class="border-0"
                button
                @click="openEdit(data.item)"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("actions.edit") }}
                </span>
              </b-list-group-item>

              <b-list-group-item
                v-if="$can('update', 'Intelbras')"
                class="border-0"
                button
                @click="confirmDelete(data.item.id)"
              >
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("actions.delete") }}
                </span>
              </b-list-group-item>

              <b-list-group-item
                v-if="$can('update', 'Intelbras')"
                class="border-0"
                button
                @click="restart(data.item)"
              >
                <feather-icon icon="RepeatIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("intelbras.rebuild") }}
                </span>
              </b-list-group-item>

              <b-list-group-item
                v-if="$can('update', 'Intelbras')"
                class="border-0"
                button
                @click="reconnect(data.item)"
              >
                <feather-icon icon="RssIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("intelbras.reconnect") }}
                </span>
              </b-list-group-item>

              <b-list-group-item
                v-if="$can('update', 'Intelbras')"
                class="border-0"
                button
                @click="syncDevices(data.item)"
              >
                <feather-icon icon="RefreshCwIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("actions.sync") }} {{ $t("intelbras.devices") }}
                </span>
              </b-list-group-item>
            </b-list-group>
          </b-popover>
        </template>
      </b-table>
      <!-- Footer Table -->
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted text-capitalize">{{
              $t("table.showing", {
                from: dataMeta.from,
                to: dataMeta.to,
                of: dataMeta.of,
              })
            }}</span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <Pagination
              :totalList="totalList"
              :currentPage="currentPage"
              @updatePagination="update"
              :perPage="perPage"
            >
            </Pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BPagination,
  BInputGroup,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref, onMounted } from "@vue/composition-api";
import useList from "./useList";
import Sidenav from "./Sidenav.vue";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import ToastificationProgressContent from "@core/components/toastification/ToastificationProgressContent.vue";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import Pagination from "@/components/Pagination.vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import Filters from "./Filters.vue";

export default {
  components: {
    Sidenav,
    Pagination,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BInputGroup,
    vSelect,
    ToastificationLoadingContent,
    ToastificationProgressContent,
    BreadcrumbMenu,
    FilterModal,
    ButtonFilters,
    Filters,
  },

  setup(_, context) {
    //start pagination
    const update = (data) => {
      currentPage.value = data;
    };
    //end pagination

    const loadingToast = ref(null);
    const isSidebarActive = ref(false);
    const itemEdit = ref({});
    const isAdd = ref(false);
    const loading = ref(false);

    const openEdit = (item) => {
      isSidebarActive.value = true;
      itemEdit.value = JSON.parse(JSON.stringify(item));
      isAdd.value = false;
    };
    const { t } = useI18nUtils();

    const toast = useToast();

    const trans = {
      "messages.delete.title": t("messages.delete.title"),
      "messages.delete.body": t("messages.delete.body"),
      "actions.delete": t("actions.delete"),
      "actions.cancel": t("actions.cancel"),
      "intelbras.actions": t("intelbras.actions"),
      "intelbras.syncCorrectly": t("intelbras.syncCorrectly"),
      "intelbras.events": t("intelbras.events"),
      "intelbras.status": t("intelbras.status"),
      "intelbras.devices": t("intelbras.devices"),
      "intelbras.syncDevices": t("intelbras.syncDevices"),
      "intelbras.syncing": t("intelbras.syncing"),
      "intelbras.withoutProfile": t("intelbras.withoutProfile"),
      "intelbras.arm": t("intelbras.arm"),
      "intelbras.disarm": t("intelbras.disarm"),
      "intelbras.arming": t("intelbras.arming"),
      "intelbras.disarming": t("intelbras.disarming"),
      "intelbras.armedCorrectly": t("intelbras.armedCorrectly"),
      "intelbras.disarmedCorrectly": t("intelbras.disarmedCorrectly"),
      "intelbras.panel": t("intelbras.panel"),
      "messages.copy.copied_successfully": t("messages.copy.copied_successfully"),
      "messages.copy.could_not_copy": t("messages.copy.could_not_copy"),
    };

    const {
      // methods
      fetchList,
      refetchData,
      clearSearchFilters,
      buildFilters,

      //table pagination
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,

      // filters
      centerFilter,
      sectorFilter,
      facilitiesFilter,

      // Options
      facilitiesOptions,
      centerOptions,
      sectorOptions,

      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      formatDateAssigned,
      filtersCount,
    } = useList();

    const confirmDelete = function (id) {
      const self = this;
      const storeInner = store;
      this.$swal({
        title: trans["messages.delete.title"],
        text: trans["messages.delete.body"],
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: trans["actions.delete"],
        cancelButtonText: trans["actions.cancel"],
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          storeInner
            .dispatch("intelbras_panels/delete", id)
            .then((response) => {
              if (response.success) {
                toast({
                  component: ToastificationContent,
                  props: {
                    title: response.message,
                    icon: "CheckIcon",
                    variant: "success",
                  },
                });
                self.refetchData();
              } else {
                toast(
                  {
                    component: ToastificationContent,
                    props: {
                      title: response.message,
                      icon: "AlertTriangleIcon",
                      variant: "danger",
                    },
                  },
                  {
                    timeout: 10000,
                  }
                );
              }
            })
            .catch((response) => {
              toast(
                {
                  component: ToastificationContent,
                  props: {
                    title: response.response.data.message,
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                  },
                },
                {
                  timeout: 10000,
                }
              );
            });
        }
      });
    };

    const openAdd = () => {
      isAdd.value = true;
      isSidebarActive.value = true;
    };

    const copyToClipboard = (id, field) => {
      store
        .dispatch("intelbras_panels/copyPassword", { id, field })
        .then((response) => {
          navigator.clipboard.writeText(response.data.value);
          toast({
            component: ToastificationContent,
            props: {
              title: "Copiado correctamente",
              icon: "ClipboardIcon",
              variant: "success",
            },
          });
        })
        .catch((error) => {
          toast({
            component: ToastificationContent,
            props: {
              title: "No se pudo copiar",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const onError = () => {
      toast({
        component: ToastificationContent,
        props: {
          title: trans["messages.copy.could_not_copy"],
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    };

    const unitsSite = (item) => {
      let { units } = item.device;

      if (units.length) {
        let unitsDescription = units.map((unit) => unit.description);

        return unitsDescription.join(",");
      }

      return "";
    };

    const armPanel = function (panel) {
      const messageAlert = panel.active
        ? `¿${trans["intelbras.disarm"]} ${panel.name}?`
        : `¿${trans["intelbras.arm"]} ${panel.name}?`;

      const confirmButton = panel.active
        ? trans["intelbras.disarm"]
        : trans["intelbras.arm"];

      this.$swal({
        title: messageAlert,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: confirmButton,
        cancelButtonText: trans["actions.cancel"],
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.value) {
          let { device } = panel;
          let params = {
            panel_id: device.id,
            action: panel.active ? "disarm" : "arm",
          };

          let descriptionToast = panel.active
            ? `${trans["intelbras.disarm"]} ${trans["intelbras.panel"]}`
            : `${trans["intelbras.arm"]} ${trans["intelbras.panel"]}`;

          let descriptionContent = panel.active
            ? `${trans["intelbras.disarming"]} ${trans["intelbras.panel"]} ${panel.name}`
            : `${trans["intelbras.arming"]} ${trans["intelbras.panel"]} ${panel.name}`;
          loadingToast.value.show(descriptionToast, descriptionContent, "secondary");

          loading.value = true;

          await store
            .dispatch("intelbras_panels/arm", params)
            .then(() => {
              const messageToast = panel.active
                ? `${trans["intelbras.panel"]} ${panel.name} ${trans["intelbras.disarmedCorrectly"]}`
                : `${trans["intelbras.panel"]} ${panel.name} ${trans["intelbras.armedCorrectly"]}`;

              toast({
                component: ToastificationContent,
                props: {
                  title: messageToast,
                  icon: "CheckIcon",
                  variant: "success",
                },
              });

              refetchData();
            })
            .catch((error) => {
              const errorMessage = error.response.data?.message;
              toast({
                component: ToastificationContent,
                props: {
                  title: errorMessage,
                  icon: "AlertTriangleIcon",
                  variant: "danger",
                },
              });
            })
            .finally(() => {
              loadingToast.value.close();
              loading.value = false;
            });
        }
      });
    };

    const syncActions = async (id) => {
      await store
        .dispatch("intelbras_panels/syncActions", id)
        .then(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: `${trans["intelbras.actions"]} ${trans["intelbras.syncCorrectly"]}`,
              icon: "CheckIcon",
              variant: "success",
            },
          });
        })
        .catch((error) => {
          const errorMessage = error.response.data?.message;
          console.log("error", error.response.data);

          toast({
            component: ToastificationContent,
            props: {
              title: errorMessage,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const syncEvents = async (id) => {
      await store
        .dispatch("intelbras_panels/syncEvents", id)
        .then(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: `${trans["intelbras.events"]} ${trans["intelbras.syncCorrectly"]}`,
              icon: "CheckIcon",
              variant: "success",
            },
          });
        })
        .catch((error) => {
          const errorMessage = error.response.data?.message;

          toast({
            component: ToastificationContent,
            props: {
              title: errorMessage,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const syncStatus = async (id) => {
      await store
        .dispatch("intelbras_panels/syncStatus", id)
        .then(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: `${trans["intelbras.status"]} ${trans["intelbras.syncCorrectly"]}`,
              icon: "CheckIcon",
              variant: "success",
            },
          });
        })
        .catch((error) => {
          const errorMessage = error.response.data?.message;
          toast({
            component: ToastificationContent,
            props: {
              title: errorMessage,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const syncDevices = async (panel) => {
      let descriptionToast = `${trans["intelbras.syncDevices"]}`;
      let descriptionContent = `${trans["intelbras.syncing"]}`;

      loadingToast.value.show(descriptionToast, descriptionContent, "secondary");

      await store
        .dispatch("intelbras_panels/syncDevices", panel.id)
        .then(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: `${trans["intelbras.devices"]} ${trans["intelbras.syncCorrectly"]}`,
              icon: "CheckIcon",
              variant: "success",
            },
          });
        })
        .catch((error) => {
          const errorMessage = error.response.data?.message || error.message;
          toast({
            component: ToastificationContent,
            props: {
              title: errorMessage,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });

      loadingToast.value.close();
    };

    const restart = async (panel) => {
      let params = {
        uuid: panel.uuid,
        instance_host: panel.instance_host,
      };

      await store
        .dispatch("intelbras_panels/restart", params)
        .then((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: `${response.message}`,
              icon: "CheckIcon",
              variant: "success",
            },
          });

          refetchData();
        })
        .catch((error) => {
          const errorMessage = error.response.data?.message;

          toast({
            component: ToastificationContent,
            props: {
              title: errorMessage,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const reconnect = async (panel) => {
      let params = {
        panel_id: panel.uuid,
        instance_host: panel.instance_host,
        data: {},
      };

      await store
        .dispatch("intelbras_panels/reconnect", params)
        .then((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: `${response.message}`,
              icon: "CheckIcon",
              variant: "success",
            },
          });

          refetchData();
        })
        .catch((error) => {
          const errorMessage = error.response.data?.message;

          toast({
            component: ToastificationContent,
            props: {
              title: errorMessage,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const onCopy = () => {
      toast({
        component: ToastificationContent,
        props: {
          title: trans["messages.copy.copied_successfully"],
          icon: "ClipboardIcon",
          variant: "success",
        },
      });
    };

    return {
      isSidebarActive,
      isAdd,
      itemEdit,
      openEdit,
      openAdd,
      confirmDelete,
      update,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,

      centerFilter,
      sectorFilter,
      facilitiesOptions,
      centerOptions,
      sectorOptions,
      facilitiesFilter,

      copyToClipboard,
      onCopy,
      onError,

      filtersCount,
      buildFilters,
      clearSearchFilters,
      unitsSite,
      loadingToast,
      loading,

      armPanel,
      syncEvents,
      syncActions,
      syncStatus,
      syncDevices,
      restart,
      reconnect,
    };
  },
};
</script>

<style scoped>
.btn-orange {
  background-color: orange;
  color: white;
}

.btn-orange:hover {
  box-shadow: 0 8px 25px -8px orange;
}
</style>
